import axios, { AxiosError, isAxiosError, type InternalAxiosRequestConfig } from 'axios'
import { msalInstance, requestConfig, state } from '@/config/msalConfig'
import { InteractionRequiredAuthError, type AuthenticationResult } from '@azure/msal-browser'
import { createDiscreteApi } from "naive-ui"
import { useAuth } from './useAuth'

const { message, notification, loadingBar } = createDiscreteApi([ 'message', 'notification', 'loadingBar' ])

export interface Options {
  showFeedback?: boolean
  successMessage?: string
  errorMessage?: string
}

export async function checkHealth() {
  await fetch(import.meta.env.VITE_API_ENDPOINT + '/healthz/liveness')
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_ENDPOINT + '/api'
})

api.interceptors.request.use(async (config) => {
  await handleAuth(config)

  return config
}, (error) => {
  return Promise.reject(error)
})

api.interceptors.response.use((response) => {
  return response
}, (error: AxiosError) => {
  console.log('Bad response:', error)

  if (isAxiosError(error)) {
    console.log('Cause', error.cause)
    console.log('Code', error.code)
    console.log('Response', error.response?.status)
    console.log('Message', error.message)

    if (error.response?.status === 401) {
      // User must sign in again
      useAuth().login()
    }

    else {
      console.debug('Error is not 401')
      return Promise.reject(error)
    }
  }

  else {
    console.debug('Error is NOT axios')
    return Promise.reject(error)
  }
})

export async function useFetch (url: string, query?: any): Promise<any> {
  loadingBar.start()

  try {
    const { data } = await api.get(url, {
      params: query,
      paramsSerializer: {
        indexes: null
      }
    })

    return data
  } catch (error) {
    console.error(error)
    loadingBar.error()

    if (isAxiosError(error)) {
      if (error.response?.data?.type === 'SqlException' && error.response?.data?.detail?.toLowerCase().includes('timeout')) {
        notification.error({
          title: 'Operation timeout',
          content: 'The request took too long to complete and has been terminated. Please try again by searching for a smaller data set.',
          duration: 10000
        })
      } else {
        message.error('Something went wrong!')
      }
    } else {
      message.error('Something went wrong!')
    }

    throw error
  } finally {
    loadingBar.finish()
  }
}

export async function useUpdate (
  url: string,
  model: any,
  {
    showFeedback = true,
    successMessage = 'Changes were saved',
    errorMessage = 'Yikes! Changes were not saved'
  }: Options = {}
): Promise<any> {
  loadingBar.start()

  try {
    const { data } = await api.put(url, model)

    if (showFeedback) message.success(successMessage)
    return data
  } catch (error) {
    console.error(error)
    loadingBar.error()
    if (showFeedback) message.error(errorMessage)
    throw error
  } finally {
    loadingBar.finish()
  }
}

/**
 * 
 * @param url The URL to send the request to.
 * @param model Whatever model you want to send. Leave empty if nothing should be sent.
 * @param options Configure how the method behaves.
 * @returns 
 */
export async function usePatch(
  url: string,
  model?: any,
  {
    showFeedback = true,
    successMessage = 'Changes were saved',
    errorMessage = 'Yikes! Changes were not saved'
  }: Options = {}
): Promise<any> {
  loadingBar.start()

  try {
    const { data } = await api.patch(url, model)

    if (showFeedback) message.success(successMessage)
    return data
  } catch (error) {
    console.error(error)
    loadingBar.error()
    if (showFeedback) message.error(errorMessage)
    throw error
  } finally {
    loadingBar.finish()
  }
}

export async function useCreate (
  url: string,
  model: unknown,
  {
    showFeedback = true,
    successMessage = 'Changes were saved',
    errorMessage = 'Yikes! Changes were not saved'
  }: Options = {}
) {
  loadingBar.start()

  try {
    const { data } = await api.post(url, model)

    if (showFeedback) message.success(successMessage)
    return data
  } catch (error) {
    console.error(error)
    loadingBar.error()
    if (showFeedback) message.error(errorMessage)
    throw error
  } finally {
    loadingBar.finish()
  }
}

export async function useDelete (
  url: string,
  {
    showFeedback = true,
    successMessage = 'Deletion successful',
    errorMessage = 'Deletion failed'
  }: Options = {}
) {
  loadingBar.start()

  try {
    const { data } = await api.delete(url)

    if (showFeedback) message.success(successMessage)
    return data
  } catch (error) {
    console.error(error)
    loadingBar.error()
    if (showFeedback) message.error(errorMessage)
    throw error
  } finally {
    loadingBar.finish()
  }
}

async function handleAuth(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
  // Some routes do not need a token and should be filtered out of the interceptor
  if (config.url?.startsWith('cmd')) return config

  else {
    try {
      const result: AuthenticationResult = await msalInstance.acquireTokenSilent(requestConfig)
      config.headers.Authorization = `Bearer ${result.accessToken}`

      if (!state.roles.length) {
        assignRoles(result.accessToken)
      }
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await msalInstance.acquireTokenRedirect(requestConfig)
      }
    }

    return config
  }
}

function parseJwt (token: string): any {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(''));

  return JSON.parse(jsonPayload);
}

function assignRoles(token: string) {
  const parsed = parseJwt(token)

  state.roles = parsed.roles
  state.isAdmin = parsed.roles.map((role: string) => role.toLowerCase()).includes('cdi.admin')
}

// function isDateString(value: unknown): boolean {
//   const dateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}.\d{2}.\d{2}Z/;
//   return typeof value === 'string' && dateFormat.test(value)
// }

// function convertToJSDate(data: unknown) {
//   if (data === null || data === undefined || typeof data !== 'object') {
//     return data
//   }

//   for (const [key, val] of Object.entries(data)) {
//     if (isDateString(val)) {
//       const converted = String(val).replace(/\./g, ':')
//       // @ts-expect-error
//       data[key] = Date.parse(converted)
//     }

//     else if (typeof val === 'object') {
//       convertToJSDate(val)
//     }
//   }

//   return data
// }

// function convertToDateString(data: unknown) {
//   if (data === null || data == undefined || typeof data !== 'object') {
//     return data
//   }

//   for (const [key, val] of Object.entries(data)) {
//     if (val instanceof Date) {
//       // @ts-expect-error
//       data[key] = val.toISOString()
//     }
//   }

//   return data
// }
